import React, { useState, useEffect } from 'react';
import { X, MapPin, Plus, CheckCircle, Trash2, Phone, User, DollarSign, Package, Truck, File, Navigation, Locate, Camera } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import GooglePlacesInput from './GooglePlacesInput';
import FrequentStopsSelector from './FrequentStopsSelector';

const AdditionalStopsModal = ({ isOpen, onClose, routeId, isMessenger = false }) => {
    const [stops, setStops] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({
        stop_type: 'other',
        address: '',
        latitude: null,
        longitude: null,
        contact_name: '',
        contact_phone: '',
        description: '',
        payment_amount: ''
    });
    const [saveAsFrequent, setSaveAsFrequent] = useState(false);
    const [frequentName, setFrequentName] = useState('');

    const stopTypes = [
        { value: 'pickup', label: 'Recogida', icon: Package },
        { value: 'delivery', label: 'Entrega', icon: Truck },
        { value: 'errand', label: 'Diligencia', icon: File },
        { value: 'other', label: 'Otro', icon: MapPin }
    ];

    useEffect(() => {
        if (isOpen && routeId) {
            loadStops();
        }
    }, [isOpen, routeId]);

    const loadStops = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/routes/${routeId}/additional-stops`);
            setStops(response.data.stops || []);
        } catch (error) {
            console.error('Error loading stops:', error);
            toast.error('Error al cargar paradas');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.address.trim()) {
            toast.error('La dirección es obligatoria');
            return;
        }

        // Validate frequent stop name if saving
        if (saveAsFrequent && !frequentName.trim()) {
            toast.error('Ingresa un nombre para la parada frecuente');
            return;
        }

        // 📍 GPS OBLIGATORIO para crear parada adicional
        let gpsCoords = null;
        if (isMessenger) {
            try {
                const pos = await new Promise((resolve, reject) => {
                    if (!navigator.geolocation) return reject(new Error('GPS no soportado'));
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true, timeout: 10000, maximumAge: 0
                    });
                });
                gpsCoords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
            } catch (gpsErr) {
                toast.error('📍 Debes activar el GPS para crear una parada adicional.', { duration: 5000 });
                return;
            }
        }

        try {
            setLoading(true);

            // Create additional stop
            await api.post(`/routes/${routeId}/additional-stops`, {
                ...formData,
                latitude: gpsCoords?.latitude || formData.latitude || null,
                longitude: gpsCoords?.longitude || formData.longitude || null,
                payment_amount: parseFloat(formData.payment_amount) || 0
            });

            toast.success('✅ Parada adicional creada');

            // Save as frequent stop if requested
            if (saveAsFrequent && frequentName.trim()) {
                try {
                    await api.post('/routes/frequent-stops', {
                        name: frequentName,
                        address: formData.address,
                        phone: formData.contact_phone,
                        observations: formData.description,
                        latitude: formData.latitude,
                        longitude: formData.longitude
                    });
                    toast.success('💾 Guardada como parada frecuente');
                } catch (error) {
                    console.error('Error saving frequent stop:', error);
                    toast.error('No se pudo guardar como frecuente');
                }
            }

            // Reset form
            setFormData({
                stop_type: 'other',
                address: '',
                latitude: null,
                longitude: null,
                contact_name: '',
                contact_phone: '',
                description: '',
                payment_amount: ''
            });
            setSaveAsFrequent(false);
            setFrequentName('');
            setShowAddForm(false);
            loadStops();
        } catch (error) {
            console.error('Error creating stop:', error);
            toast.error(error.response?.data?.message || 'Error al crear parada');
        } finally {
            setLoading(false);
        }
    };

    const handleComplete = async (stopId) => {
        const notes = window.prompt('Notas al completar (opcional):');
        if (notes === null) return; // User cancelled

        try {
            await api.put(`/routes/${routeId}/additional-stops/${stopId}/complete`, { notes });
            toast.success('✅ Parada completada');
            loadStops();
        } catch (error) {
            console.error('Error completing stop:', error);
            toast.error('Error al completar parada');
        }
    };

    const handleDelete = async (stopId) => {
        if (!window.confirm('¿Eliminar esta parada?')) return;

        try {
            await api.delete(`/routes/${routeId}/additional-stops/${stopId}`);
            toast.success('Parada eliminada');
            loadStops();
        } catch (error) {
            console.error('Error deleting stop:', error);
            toast.error(error.response?.data?.message || 'Error al eliminar parada');
        }
    };

    const getStopIcon = (type) => {
        const stopType = stopTypes.find(t => t.value === type);
        return stopType ? stopType.icon : MapPin;
    };

    const getStopLabel = (type) => {
        const stopType = stopTypes.find(t => t.value === type);
        return stopType ? stopType.label : type;
    };

    const handleNavigate = (stop) => {
        // Generar URL de Google Maps para navegación
        let destination;
        if (stop.latitude && stop.longitude) {
            destination = `${stop.latitude},${stop.longitude}`;
        } else {
            destination = encodeURIComponent(stop.address);
        }
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
        window.open(mapsUrl, '_blank');
    };

    if (!isOpen) return null;

    const pendingStops = stops.filter(s => s.status === 'pending');
    const completedStops = stops.filter(s => s.status === 'completed');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-purple-50">
                    <h2 className="text-xl font-bold text-gray-900">📍 Paradas Adicionales</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">

                    {/* Summary */}
                    <div className="mb-6 grid grid-cols-2 gap-4">
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="text-sm text-yellow-700">Pendientes</div>
                            <div className="text-2xl font-bold text-yellow-900">{pendingStops.length}</div>
                        </div>
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="text-sm text-green-700">Completadas</div>
                            <div className="text-2xl font-bold text-green-900">{completedStops.length}</div>
                        </div>
                    </div>

                    {/* Add Button */}
                    {!showAddForm && (
                        <button
                            onClick={() => setShowAddForm(true)}
                            className="w-full mb-4 px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 font-medium transition-colors"
                        >
                            <Plus className="w-5 h-5 inline mr-2" />
                            Agregar Parada
                        </button>
                    )}

                    {/* Add Form */}
                    {showAddForm && (
                        <form onSubmit={handleSubmit} className="mb-6 p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-gray-900">Nueva Parada</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowAddForm(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Stop Type */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
                                <select
                                    value={formData.stop_type}
                                    onChange={(e) => setFormData({ ...formData, stop_type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                >
                                    {stopTypes.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Frequent Stops Selector - Only for Logística */}
                            {!isMessenger && (
                                <FrequentStopsSelector
                                    onSelect={(stop) => {
                                        setFormData({
                                            ...formData,
                                            address: stop.address,
                                            latitude: stop.latitude,
                                            longitude: stop.longitude,
                                            contact_name: stop.name,
                                            contact_phone: stop.phone || ''
                                        });
                                        toast.success('✅ Parada frecuente seleccionada');
                                    }}
                                />
                            )}

                            {/* Address - Google Places for Logística, GPS Button for Messenger */}
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Dirección *</label>
                                    {isMessenger && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (navigator.geolocation) {
                                                    toast.loading('Capturando ubicación...', { id: 'geo' });
                                                    navigator.geolocation.getCurrentPosition(
                                                        async (position) => {
                                                            const { latitude, longitude } = position.coords;

                                                            setFormData(prev => ({
                                                                ...prev,
                                                                latitude,
                                                                longitude,
                                                                address: `Buscando dirección... (${latitude.toFixed(6)}, ${longitude.toFixed(6)})`
                                                            }));

                                                            try {
                                                                const response = await api.get(`/routes/geocoding/reverse?lat=${latitude}&lon=${longitude}`);
                                                                if (response.data.success) {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        address: response.data.address
                                                                    }));
                                                                    toast.success('📍 Ubicación y dirección capturadas', { id: 'geo' });
                                                                }
                                                            } catch (error) {
                                                                console.error('Error reverse geocoding:', error);
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    address: `GPS: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
                                                                }));
                                                                toast.success('📍 Ubicación capturada (dirección no disponible)', { id: 'geo' });
                                                            }
                                                        },
                                                        (error) => {
                                                            toast.error('No se pudo obtener ubicación', { id: 'geo' });
                                                        }
                                                    );
                                                } else {
                                                    toast.error('GPS no disponible');
                                                }
                                            }}
                                            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 flex items-center gap-1"
                                        >
                                            <Locate className="w-4 h-4" />
                                            Usar Mi Ubicación
                                        </button>
                                    )}
                                </div>
                                {!isMessenger ? (
                                    <GooglePlacesInput
                                        value={formData.address}
                                        onChange={(val) => setFormData({ ...formData, address: val })}
                                        onPlaceSelected={(place) => {
                                            setFormData({
                                                ...formData,
                                                address: place.address,
                                                latitude: place.lat,
                                                longitude: place.lng
                                            });
                                        }}
                                        placeholder="Escribe la dirección..."
                                    />
                                ) : (
                                    <textarea
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        placeholder="Dirección o usa GPS"
                                        rows={2}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    />
                                )}
                            </div>

                            {/* Save as Frequent - Only for Logística */}
                            {!isMessenger && formData.address && (
                                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={saveAsFrequent}
                                            onChange={(e) => setSaveAsFrequent(e.target.checked)}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm font-medium text-gray-700">💾 Guardar como parada frecuente</span>
                                    </label>
                                    {saveAsFrequent && (
                                        <input
                                            type="text"
                                            value={frequentName}
                                            onChange={(e) => setFrequentName(e.target.value)}
                                            placeholder="Nombre para la parada (ej: Proveedor Merengones)"
                                            className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                        />
                                    )}
                                </div>
                            )}

                            {/* Contact Name */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Contacto</label>
                                <input
                                    type="text"
                                    value={formData.contact_name}
                                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                                    placeholder="Nombre del contacto"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            {/* Contact Phone */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                                <input
                                    type="tel"
                                    value={formData.contact_phone}
                                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                                    placeholder="3001234567"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            {/* Description */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Detalles adicionales"
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            {/* Payment Amount (only visible for logística/admin) */}
                            {!isMessenger && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Pago al Mensajero</label>
                                    <input
                                        type="number"
                                        value={formData.payment_amount}
                                        onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                                        placeholder="0"
                                        step="100"
                                        min="0"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                            )}

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple700 disabled:opacity-50 font-medium"
                            >
                                {loading ? 'Guardando...' : 'Guardar Parada'}
                            </button>
                        </form>
                    )}

                    {/* Stops List */}
                    {loading && stops.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">Cargando...</div>
                    ) : stops.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No hay paradas adicionales</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Pending Stops */}
                            {pendingStops.length > 0 && (
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">⏳ Pendientes ({pendingStops.length})</h3>
                                    <div className="space-y-3">
                                        {pendingStops.map((stop) => {
                                            const Icon = getStopIcon(stop.stop_type);
                                            return (
                                                <div key={stop.id} className="p-4 border-2 border-yellow-200 rounded-lg bg-yellow-50">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-start gap-3 flex-1">
                                                            <div className="p-2 bg-yellow-200 rounded-lg">
                                                                <Icon className="w-5 h-5 text-yellow-700" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="font-semibold text-gray-900">{getStopLabel(stop.stop_type)}</div>
                                                                <div className="text-sm text-gray-700 mt-1">{stop.address}</div>
                                                                {stop.contact_name && (
                                                                    <div className="text-xs text-gray-600 mt-1">
                                                                        <User className="w-3 h-3 inline mr-1" />{stop.contact_name}
                                                                    </div>
                                                                )}
                                                                {stop.contact_phone && (
                                                                    <div className="text-xs text-gray-600">
                                                                        <Phone className="w-3 h-3 inline mr-1" />{stop.contact_phone}
                                                                    </div>
                                                                )}
                                                                {stop.description && (
                                                                    <div className="text-sm text-gray-600 mt-2 italic">{stop.description}</div>
                                                                )}
                                                                {stop.payment_amount > 0 && (
                                                                    <div className="text-sm text-green-700 mt-1 font-semibold">
                                                                        <DollarSign className="w-4 h-4 inline" />
                                                                        ${parseFloat(stop.payment_amount).toLocaleString('es-CO')}
                                                                    </div>
                                                                )}
                                                                <div className="text-xs text-gray-500 mt-2">
                                                                    Creada por {stop.created_by_name} • {new Date(stop.created_at).toLocaleString('es-CO')}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col gap-2 ml-4">
                                                            {/* Botón de Navegación */}
                                                            <button
                                                                onClick={() => handleNavigate(stop)}
                                                                className="px-3 py-2 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 flex items-center gap-1"
                                                                title="Navegar con Google Maps"
                                                            >
                                                                <Navigation className="w-4 h-4" />
                                                                Ir
                                                            </button>
                                                            {isMessenger && (
                                                                <button
                                                                    onClick={() => handleComplete(stop.id)}
                                                                    className="px-3 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                                                    title="Completar parada"
                                                                >
                                                                    <CheckCircle className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleDelete(stop.id)}
                                                                className="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                                                                title="Eliminar parada"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Completed Stops */}
                            {completedStops.length > 0 && (
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">✅ Completadas ({completedStops.length})</h3>
                                    <div className="space-y-3">
                                        {completedStops.map((stop) => {
                                            const Icon = getStopIcon(stop.stop_type);
                                            return (
                                                <div key={stop.id} className="p-4 border border-green-200 rounded-lg bg-green-50 opacity-75">
                                                    <div className="flex items-start gap-3">
                                                        <div className="p-2 bg-green-200 rounded-lg">
                                                            <Icon className="w-5 h-5 text-green-700" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="font-semibold text-gray-900">{getStopLabel(stop.stop_type)}</div>
                                                            <div className="text-sm text-gray-700 mt-1">{stop.address}</div>
                                                            {stop.notes && (
                                                                <div className="text-sm text-gray-600 mt-2 italic">Notas: {stop.notes}</div>
                                                            )}
                                                            <div className="text-xs text-gray-500 mt-2">
                                                                Completada por {stop.completed_by_name} • {new Date(stop.completed_at).toLocaleString('es-CO')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
                    >
                        Cerrar
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AdditionalStopsModal;
